import { useState } from 'react'
import {
  AspectChip,
  Button,
  Card,
  IconButton,
  Input,
  OrderButtons,
} from '../../../design/components'
import { useSceneSession, useSceneSessionStore } from '../sessionState'
import type { SceneAspectsSection } from '../types'
import type { SceneEditing } from '../useSceneEditing'
import { moved } from '../../../lib/order'
import styles from './play.module.css'

/**
 * Design spec §5.3's scene aspects — "Pouring Rain ●2", blood-tinted when hostile.
 *
 * This card is where the two halves of the tracker meet, and the seam is worth being
 * explicit about. **The aspect and its wording come from the API; the free-invoke count
 * and the hostile flag do not.** `SceneAspectDto` is `(Id, Description, Order)` — the
 * design spec's `freeInvokes` and `hostile` have no column and no endpoint, so they live
 * in `sessionState.ts`, keyed by aspect id, on this device only.
 *
 * The consequence a reader has to be told: removing an aspect server-side leaves its
 * counter orphaned in this tab. That is harmless (nothing reads a counter for an aspect
 * that is gone) and it is the honest cost of the split.
 */
export function SceneAspectsCard({
  sceneId,
  aspects,
  editing,
}: {
  sceneId: number
  aspects: SceneAspectsSection | undefined
  editing: SceneEditing
}) {
  const [adding, setAdding] = useState(false)
  const [description, setDescription] = useState('')

  const session = useSceneSession(sceneId)
  const setFreeInvokes = useSceneSessionStore((state) => state.setFreeInvokes)
  const toggleHostile = useSceneSessionStore((state) => state.toggleHostile)

  if (aspects === undefined) return null

  const all = aspects.aspects ?? []
  const canEdit = editing.canEdit

  return (
    <Card
      variant="aspects"
      label="Scene aspects"
      hint={all.length === 1 ? '1 in play' : `${all.length} in play`}
      action={
        canEdit && !adding ? (
          <IconButton label="Add an aspect" icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      {all.length === 0 && !adding && <p className={styles.empty}>Nothing in play yet.</p>}

      {all.map((aspect, index) => {
        const count = session.freeInvokes[aspect.id] ?? 0
        const hostile = session.hostile.includes(aspect.id)

        return (
          <div key={aspect.id} className={styles.aspectRow}>
            <AspectChip
              text={aspect.description}
              variant={hostile ? 'hostile' : 'neutral'}
              {...(count > 0 ? { count } : {})}
            />

            <span className={styles.aspectControls}>
              {/* The counters are everyone's, not just the GM's: they are device-local,
                  so there is no server-side permission to respect and a player tracking
                  their own table's invokes harms nobody. */}
              <button
                type="button"
                className={styles.stepper}
                aria-label={`Remove a free invoke from ${aspect.description}`}
                disabled={count === 0}
                onClick={() => setFreeInvokes(sceneId, aspect.id, count - 1)}
              >
                −
              </button>
              <button
                type="button"
                className={styles.stepper}
                aria-label={`Add a free invoke to ${aspect.description}`}
                onClick={() => setFreeInvokes(sceneId, aspect.id, count + 1)}
              >
                +
              </button>
              <Button
                variant="secondary"
                aria-pressed={hostile}
                onClick={() => toggleHostile(sceneId, aspect.id)}
              >
                {hostile ? 'Hostile' : 'Mark hostile'}
              </Button>

              {canEdit && (
                <>
                  <OrderButtons
                    label={aspect.description}
                    index={index}
                    total={all.length}
                    disabled={editing.reorderAspects.isPending}
                    onMove={(to) =>
                      editing.reorderAspects.mutate({
                        ids: moved(
                          all.map((a) => a.id),
                          index,
                          to,
                        ),
                      })
                    }
                  />
                  <Button
                    variant="danger"
                    disabled={editing.removeAspect.isPending}
                    onClick={() => editing.removeAspect.mutate({ aspectId: aspect.id })}
                  >
                    Remove
                  </Button>
                </>
              )}
            </span>
          </div>
        )
      })}

      {canEdit && all.length > 0 && (
        <p className={styles.note}>
          Free-invoke counts and the hostile tint are kept on this device — the API has no field for
          either.
        </p>
      )}

      {canEdit && adding && (
        <form
          className={styles.inlineForm}
          onSubmit={(event) => {
            event.preventDefault()
            editing.addAspect.mutate(
              { description },
              {
                onSuccess: () => {
                  setDescription('')
                  setAdding(false)
                },
              },
            )
          }}
        >
          <Input
            label="Aspect"
            placeholder="Pouring rain"
            autoFocus
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={editing.addAspect.isPending || description.trim() === ''}
            >
              {editing.addAspect.isPending ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </form>
      )}
    </Card>
  )
}
